<script setup lang="ts">
//#region imports
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { cloneDeep, get, isNaN, toNumber } from 'lodash-es';
import { useFilters } from '@/setup/filters';
import { useApi } from '@/api';
import { useTypedStore } from '@/store';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useLs } from '@/setup/vueLs';
import { useFormatCurrency } from '@/composables/useFormatCurrency';
import { useUpdateUrl } from '@/composables/useUpdateUrl';
import { useEventBus } from '@/setup/eventBus';
import type {
  GetPlayersByIdResponse,
  GetPlayersByIdStatsResponse,
} from '@/types/openapi-ts';
import type { CardItemType } from '@/components/CardComponents/CardItem.vue';
//#endregion

//#region types
type PlayerInfo = Partial<GetPlayersByIdResponse['payload']>;

type StatFormat = 'number' | 'percent' | 'currency';
type PlayerStatItem = {
  stat_name?: string;
  stat_count?: number | string;
  stat_percent?: number | string;
  stat_sum?: number | string;
};

interface VisitAttribute {
  key: string;
  name: string;
  value: string;
  isLink?: boolean;
}
//#endregion

//#region define
const info = defineModel<PlayerInfo>('info', {
  default: () => ({}),
});
//#endregion

//#region const
const LS_KEY = 'PlayerInfo';
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const filters = useFilters();
const eventBus = useEventBus();
const ls = useLs();
const { currencyCode, checkCurrencyCode, formatCurrency } = useFormatCurrency();
const popupManager = usePopupManager();
const updateUrl = useUpdateUrl();
//#endregion

//#region state
const stats = ref<Partial<GetPlayersByIdStatsResponse['payload']>>({});
const isStatsLoading = ref(false);
const isInfoLoading = ref(false);
//#endregion

//#region computed
const isSuperuser = computed(() => store.getters['auth/adminAcl'].is_superuser);
const editVirtual = computed(() => calcPermissions('players_virtual_edit_settings') && !info.value.is_virtual);
const customerCurrencies = computed(() => store.getters['misc/customerCurrencies']);
const playerId = computed(() => String(route.params.id ?? ''));

const visitAttributes = computed<VisitAttribute[]>(() => {
  const player = info.value;
  const items = [
    {
      key: 'visit_ip',
      name: t('players.info.ip'),
      value: player.visit_ip,
    },
    {
      key: 'visit_referrer',
      name: t('players.info.referer'),
      value: player.visit_referrer,
      valueLink: player.visit_referrer,
    },
    {
      key: 'visit_geo',
      name: t('players.info.geo'),
      value: player.visit_country_name && player.visit_city_name
        ? `${player.visit_country_name}, ${player.visit_city_name}`
        : undefined,
    },
    {
      key: 'visit_device',
      name: t('players.info.device'),
      value: player.visit_device,
    },
    {
      key: 'visit_device_os',
      name: t('players.info.os'),
      value: player.visit_device_os,
    },
    {
      key: 'visit_device_browser',
      name: t('players.info.browser'),
      value: player.visit_device_browser,
    },
    {
      key: 'visit_isp',
      name: t('players.info.provider'),
      value: player.visit_isp,
    },
    {
      key: 'visit_connection_type',
      name: t('players.info.connection_type'),
      value: player.visit_connection_type,
    },
    {
      key: 'visit_language_name',
      name: t('players.info.language'),
      value: player.visit_language_name,
    },
  ];

  if(info.value.last_active_at) {
    items.unshift({
      key: 'last_active_at',
      name: t('players.info.last_active'),
      value: filters.formatDate4(info.value.last_active_at),
    });
  }

  return items.filter((item): item is VisitAttribute => !!item.value);
});

const displayName = computed(() => {
  const firstName = info.value.first_name;
  const lastName = info.value.last_name;
  if (firstName === undefined && lastName === undefined) {
    return 'N/A';
  }
  if (firstName || lastName) {
    return [firstName, lastName].filter(Boolean).join(' ');
  }
  return t('affiliates.cardView.emptyField');
});

const canEditAffiliate = computed(() => {
  return calcPermissions('players_assign_affiliate')
    && info.value.affiliate_id !== undefined
    && !info.value.is_virtual
    && info.value.is_virtual !== undefined;
});

const siteId = computed(() => {
  const raw = route.query.param_playerlist;
  if (typeof raw !== 'string' || !raw) {
    return '';
  }
  const back = raw.replace(/back=/gi, '');
  const queryString = back.includes('?')
    ? back.slice(back.indexOf('?') + 1)
    : '';
  return new URLSearchParams(queryString).get('site_id') ?? '';
});
//#endregion

//#region methods
function calcPermissions(item: string) {
  const currentAcl = store.getters['auth/currentAcl'];

  return currentAcl[item as keyof typeof currentAcl] === 'allow'
    || (
      isSuperuser.value
      && !localStorage.getItem('active-team')
    );
}

function checkSensitiveAction(action: string, row?: unknown) {
  eventBus.$emit('checkSensitiveAction', action, row);
}

function openLinkAffiliate() {
  popupManager.linkAffiliate.open({
    data: { player: info.value, siteId: siteId.value },
    listeners: {
      success: () => fetchData(),
    },
  });
}

function openMarkPlayerVirtual() {
  popupManager.markPlayerVirtual.open({
    data: {
      playerId: info.value.id || '',
      sitePlayerId: info.value.site_player_id || '',
      isVirtual: info.value.is_virtual ?? false,
    },
    listeners: {
      success: () => fetchData(),
    },
  });
}

function fetchData() {
  if (!playerId.value) {
    return;
  }
  isInfoLoading.value = true;
  api.getPlayerInfo(playerId.value)
    .then((res) => {
      info.value = res.data.payload;
    })
    .finally(() => {
      isInfoLoading.value = false;
    });
}

function fetchStats(nextCurrencyCode?: string) {
  if (!playerId.value) {
    return;
  }
  isStatsLoading.value = true;

  if (nextCurrencyCode) {
    currencyCode.value = nextCurrencyCode;
  }

  const currency = currencyCode.value;
  if (!currency) {
    isStatsLoading.value = false;
    return;
  }

  api
    .getPlayerStats(playerId.value, {
      currency_code: currency,
    })
    .then((response) => {
      stats.value = cloneDeep(response.data.payload);
    })
    .finally(() => {
      isStatsLoading.value = false;
    });
}

function initCurrencyFromUrl() {
  const urlData = updateUrl.getParseParams(route.hash);
  currencyCode.value = checkCurrencyCode(get(urlData, 'currency_code', ls.get(`${LS_KEY}/currency_code`)));
}

function cardBlockFormat(block?: PlayerStatItem[] | null) {
  const result: CardItemType[] = [];
  if (block) {
    block.forEach((item) => {
      const value = `${formatStat(item.stat_count, 'number')}  / `
        + `${formatStat(item.stat_percent, 'percent')} / `
        + `${formatStat(item.stat_sum, 'currency')}`;
      result.push({
        name: item.stat_name,
        value: value,
      });
    });
  }
  return result;
}

function formatStat(value: number | string | undefined, format: StatFormat) {
  if (value === undefined || value === null || value === '') {
    return 'N/A';
  }
  const numeric = toNumber(value);
  if (isNaN(numeric)) {
    return 'N/A';
  }

  switch (format) {
    case 'number':
      return filters.formatMoneyNoFrac(String(numeric));
    case 'percent':
      return `${filters.formatMoneyNoFrac(String(numeric))}%`;
    case 'currency':
      return formatCurrency(numeric);
    default:
      return value;
  }
}

function query() {
  return {
    currency_code: currencyCode.value,
    param_playerlist: route.query.param_playerlist,
  };
}

function completedFilterUrl() {
  updateUrl.updateGetParams(query());
}
//#endregion

//#region watch
watch(currencyCode, (val) => {
  ls.set(`${LS_KEY}/currency_code`, val);
  completedFilterUrl();
});

watch(playerId, (value) => {
  if (!value) {
    router.push('/players/list');
    return;
  }
  stats.value = {};
  fetchStats();
});
//#endregion

//#region hooks
onBeforeMount(() => {
  initCurrencyFromUrl();
  if (!playerId.value) {
    router.push('/players/list');
    return;
  }
  eventBus.$on('clickEditAffiliateHandler', openLinkAffiliate);
  fetchStats();
});

onBeforeUnmount(() => {
  eventBus.$off('clickEditAffiliateHandler', openLinkAffiliate);
});
//#endregion
</script>

<template>
  <div class="player-info">
    <div class="t:flex t:gap-32">
      <div class="player-info__block player-info__block_side">
        <CardBlock
          :title="$t('players.info.visit_attributes')"
          :list="visitAttributes"
          is-label-ellipsis
          :is-content-ellipsis="false"
        />
      </div>
      <div class="player-info__block t:space-y-24">
        <CardBlock
          :title="$t('players.info.personal_info')"
        >
          <template v-slot:actionOuter>
            <div
              class="t:flex t:gap-8"
            >
              <UiStatusBlock
                :title="$t('players.info.promo_restrictions')"
                :status="info.is_restricted_promo ? 'declined' : 'approved'"
                :status-text="$t(`ui.filters.select.${info.is_restricted_promo ? 'yes' : 'no'}`)"
              />
              <UiStatusBlock
                :title="$t('players.info.blocked')"
                :status="info.is_blocked ? 'declined' : 'approved'"
                :status-text="$t(`ui.filters.select.${info.is_blocked ? 'yes' : 'no'}`)"
              />
            </div>
          </template>
          <template v-slot:cardItem>
            <CardItem
              no-border
              no-value
            >
              <template v-slot:title>
                <div class="t:space-y-4">
                  <div class="t:flex t:items-center t:space-x-8">
                    <div
                      v-if="info.created_at"
                      class="player-info__date"
                      v-html="$t(`players.info.registration_date`, {date: $filters.formatDate3(info.created_at || '')})"
                    />
                    <UiSeparator
                      v-if="info.site_registration_type_name && info.created_at"
                      type="dot"
                      color="--success-color"
                    />
                    <div
                      v-if="info.site_registration_type_name"
                      class="player-info__date"
                      v-html="$t(`players.info.registration_type`, {type: info.site_registration_type_name})"
                    />
                  </div>
                  <UiTitle
                    :font-size="24"
                    class="t:font-medium"
                  >
                    {{ displayName }}
                  </UiTitle>
                </div>
              </template>
            </CardItem>
            <UiSeparator />
            <div class="t:flex t:gap-32">
              <div class="t:flex-1">
                <CardItem
                  no-value
                  pre-icon="phone"
                  no-border
                  :item="{ name: info?.phone_number || ''}"
                />
                <CardItem
                  no-value
                  pre-icon="envelope"
                  :item="{ name: info.email || '' }"
                />
              </div>
              <div class="t:flex-1">
                <CardItem
                  no-border
                  :item="{
                    name: $t('players.info.date_of_birth'),
                    value: filters.formatDate4(info.date_of_birth || ''),
                  }"
                />
                <CardItem
                  :item="{
                    name: $t('players.info.country'),
                    value: info.player_country_name,
                  }"
                />
              </div>
            </div>
          </template>
        </CardBlock>
        <CardBlock
          v-if="info.affiliate_email !== undefined"
          :title="$t('players.info.aff')"
        >
          <template v-slot:action>
            <UiAction
              v-if="canEditAffiliate"
              icon="pen"
              :action-name="$t('players.info.edit')"
              color="var(--text-primary)"
              @click="checkSensitiveAction('clickEditAffiliateHandler')"
            />
          </template>
          <template v-slot:cardItem>
            <CardItem
              v-if="info.affiliate_email"
              no-value
              no-border
              :item="{
                name: info.affiliate_email || '',
                labelLink: `/affiliates/${info.affiliate_id}/info`
              }"
            />
            <CardItem
              v-if="info.affiliate_offer_name"
              :item="{
                name: $t('players.info.offer'),
                value: info.affiliate_offer_name,
              }"
            />
          </template>
        </CardBlock>
        <CardBlock
          v-if="info.is_virtual !== undefined"
          no-value
          :title="$t('players.info.verif_account')"
          :list="[{
            name: info.is_virtual
              ? $t('players.playersList.yes')
              : $t('players.playersList.no'),
          }]"
        >
          <template v-slot:action>
            <UiAction
              v-if="editVirtual"
              icon="pen"
              :action-name="$t('players.info.edit')"
              color="var(--text-primary)"
              @click="openMarkPlayerVirtual"
            />
          </template>
        </CardBlock>
      </div>
      <div
        v-loading="isStatsLoading"
        class="player-info__block player-info__block_side"
      >
        <div class="t:flex t:items-center t:justify-between">
          <UiLabel
            label="Statistics"
            :font-size="20"
            bold
          />
          <UiCurrencyPicker
            v-model="currencyCode"
            :currencies="customerCurrencies"
            @update:model-value="fetchStats"
          />
        </div>
        <CardBlock
          v-if="stats.top_payment_systems"
          class="t:my-16"
          :title="$t('players.info.payment_method')"
          :list="cardBlockFormat(stats.top_payment_systems)"
          is-label-ellipsis
          :is-content-ellipsis="false"
        />
        <CardBlock
          v-if="stats.bets_distribution"
          class="t:pb-12"
          :title="$t('players.info.bets')"
          :list="cardBlockFormat(stats.bets_distribution)"
          is-label-ellipsis
          :is-content-ellipsis="false"
        />
        <CardBlock
          v-if="stats.favourite_sports"
          class="t:pb-12"
          :title="$t('players.info.top_sport')"
          :list="cardBlockFormat(stats.favourite_sports)"
          is-label-ellipsis
          :is-content-ellipsis="false"
        />
        <CardBlock
          v-if="stats.favourite_leagues"
          class="t:pb-12"
          :title="$t('players.info.top_leagues')"
          :list="cardBlockFormat(stats.favourite_leagues)"
          is-label-ellipsis
          :is-content-ellipsis="false"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.player-info {
  &__block {
    flex-basis: 100%;
    &_side {
      flex-shrink: 0;
      flex-basis: 300px;
      width: 300px;
    }
  }
  &__date {
    color: var(--text-body-secondary);
    font-size: 12px;
    span {
      color: var(--text-body);
    }
  }
}
</style>
